/**
 * Compositor "satélite" que compila bajo demanda fragment shaders del estilo
 * gl-transitions. Comparte WebGL2 context y texturas A/B con el Compositor
 * principal pero gestiona su propio program cache.
 *
 * Uso:
 *   const dyn = new DynamicTransitionCompositor(gl, vbo, texA, texB);
 *   dyn.render(transitionDef, progress);
 */

import { createProgram } from "./webgl";

const VERT_DYN = `#version 300 es
in vec2 a_pos;
out vec2 v_uv;
void main() {
  v_uv = a_pos * 0.5 + 0.5;
  v_uv.y = 1.0 - v_uv.y;
  gl_Position = vec4(a_pos, 0.0, 1.0);
}
`;

function makeFragSource(body: string): string {
  return `#version 300 es
precision highp float;

uniform sampler2D u_from;
uniform sampler2D u_to;
uniform float     progress;

vec4 getFromColor(vec2 uv) { return texture(u_from, uv); }
vec4 getToColor(vec2 uv)   { return texture(u_to, uv); }

float rand(vec2 co) {
  return fract(sin(dot(co.xy, vec2(12.9898, 78.233))) * 43758.5453);
}

${body}

in vec2 v_uv;
out vec4 fragColor;
void main() {
  fragColor = transition(v_uv);
}
`;
}

interface CompiledProgram {
  program: WebGLProgram;
  uFrom: WebGLUniformLocation | null;
  uTo: WebGLUniformLocation | null;
  uProgress: WebGLUniformLocation | null;
  aPos: number;
}

export class DynamicTransitionCompositor {
  readonly gl: WebGL2RenderingContext;
  private cache = new Map<string, CompiledProgram>();
  private compileErrors = new Map<string, string>();
  private vbo: WebGLBuffer;

  constructor(gl: WebGL2RenderingContext, vbo: WebGLBuffer) {
    this.gl = gl;
    this.vbo = vbo;
  }

  has(id: string): boolean { return this.cache.has(id); }

  /** Devuelve null si el shader falló. Cachea el error para no reintentar. */
  compile(id: string, glslBody: string): CompiledProgram | null {
    if (this.cache.has(id)) return this.cache.get(id)!;
    if (this.compileErrors.has(id)) return null;
    const gl = this.gl;
    try {
      const program = createProgram(gl, VERT_DYN, makeFragSource(glslBody));
      const aPos = gl.getAttribLocation(program, "a_pos");
      const compiled: CompiledProgram = {
        program,
        uFrom:     gl.getUniformLocation(program, "u_from"),
        uTo:       gl.getUniformLocation(program, "u_to"),
        uProgress: gl.getUniformLocation(program, "progress"),
        aPos,
      };
      gl.useProgram(program);
      // sampler bindings: u_from=0 (TEXTURE0), u_to=2 (TEXTURE2)
      if (compiled.uFrom) gl.uniform1i(compiled.uFrom, 0);
      if (compiled.uTo)   gl.uniform1i(compiled.uTo,   2);
      this.cache.set(id, compiled);
      return compiled;
    } catch (err: any) {
      const msg = err?.message || String(err);
      this.compileErrors.set(id, msg);
      console.error(`Transition '${id}' compile failed:`, msg);
      return null;
    }
  }

  /** Renderiza con las texturas YA cargadas en TEXTURE0 (from) y TEXTURE2 (to). */
  render(id: string, glslBody: string, progress: number): boolean {
    const gl = this.gl;
    const compiled = this.compile(id, glslBody);
    if (!compiled) return false;
    gl.useProgram(compiled.program);
    gl.bindBuffer(gl.ARRAY_BUFFER, this.vbo);
    gl.enableVertexAttribArray(compiled.aPos);
    gl.vertexAttribPointer(compiled.aPos, 2, gl.FLOAT, false, 0, 0);
    if (compiled.uProgress) gl.uniform1f(compiled.uProgress, Math.max(0, Math.min(1, progress)));
    gl.drawArrays(gl.TRIANGLE_STRIP, 0, 4);
    return true;
  }

  dispose(): void {
    for (const c of this.cache.values()) this.gl.deleteProgram(c.program);
    this.cache.clear();
    this.compileErrors.clear();
  }
}
